/**
 * Theme Initializer
 *
 * This module handles early theme loading (before React renders)
 * to prevent flash of unstyled content (FOUC).
 *
 * Theme CSS files are served from /public/themes/ which maps to /themes/ in the browser.
 */
import {
  DEFAULT_THEME_ID,
  getThemeById,
  getThemeCssPath,
  type ThemeId,
} from '@/config/themes'
import { getCookie } from './cookies'

const CUSTOM_THEME_COOKIE_NAME = 'custom-theme-id'
const THEME_LINK_ID = 'app-theme-css'

/**
 * Initialize and load the theme synchronously before React renders.
 * This prevents flash of unstyled content (FOUC).
 *
 * Should be called in main.tsx before ReactDOM.render()
 */
export function initializeTheme(): void {
  // Get saved theme from cookie, fallback to default
  const savedThemeId = getCookie(CUSTOM_THEME_COOKIE_NAME) as ThemeId
  const themeId = savedThemeId || DEFAULT_THEME_ID

  const themeConfig = getThemeById(themeId)

  if (!themeConfig) {
    return
  }

  // Get the full CSS path using the helper
  const themePath = getThemeCssPath(themeConfig)

  // Create and inject theme CSS link immediately
  const existingLink = document.getElementById(THEME_LINK_ID) as HTMLLinkElement

  if (existingLink) {
    // Update existing link
    existingLink.setAttribute('href', themePath)
  } else {
    // Create new link element
    const link = document.createElement('link')
    link.id = THEME_LINK_ID
    link.rel = 'stylesheet'
    link.href = themePath

    // CRITICAL: Append to END of head to ensure theme loads AFTER index.css
    // This allows theme CSS variables to properly override defaults
    document.head.appendChild(link)
  }
}

/**
 * Get the currently active theme ID
 */
export function getCurrentThemeId(): ThemeId {
  const savedThemeId = getCookie(CUSTOM_THEME_COOKIE_NAME) as ThemeId
  return savedThemeId || DEFAULT_THEME_ID
}
